import { type FC, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'

import * as spriteIcons from 'assets/svg/sprite'
import { addToast } from 'components/Toast/Toast'
import ImgInCircle from 'components/UI/ImgInCircle/ImgInCircle'
import { useModalContext } from 'context/modalContext'
import SubscriptionCard from 'features/SubscriptionCard/SubscriptionCard'
import { AllIcons } from 'helpers/allIcons'
import { getSubscriptionPlans } from 'services/endpoints/settings/general/subscription'
import type { ISubscriptionPlan } from 'types/interfaces/IProfile'

import { SubscribeConfirmModal } from '../SubscribeConfirmModal/SubscribeConfirmModal'

import './_subscribe-profile-modal.scss'

type Plan = ISubscriptionPlan

const SubscribeProfileModal: FC<{
  displayName: string
  avatar: string | null
  username: string
  id: string
  subscribeFn: (payload: any) => void
  selected?: string | null
  setSelected?: (v: string) => void
  isLoading?: boolean
  campaign?: string | null
  setCampaign?: (id: string | null) => void
  initialPlans?: Plan[]
  initialCampaign?: any | null
  onClose?: () => void
  fallbackMonthlyPrice?: number | null
}> = ({
  displayName,
  avatar,
  username,
  id,
  subscribeFn,
  selected,
  setSelected,
  isLoading,
  setCampaign,
  initialPlans = [],
  initialCampaign = null,
  fallbackMonthlyPrice = null
}) => {
  const [bundlesShown, setBundlesShown] = useState(false)
  const [internalSelected, setInternalSelected] = useState<string | null>(selected ?? null)

  const modal = useModalContext()
  const { t } = useTranslation()

  const shouldFetch = initialPlans.length === 0

  const { data } = useQuery<any>(
    ['allSubsPlans_' + id, id],
    async () => {
      if (!shouldFetch) {
        return {
          subscriptions: initialPlans,
          campaign: initialCampaign
        }
      }

      return await getSubscriptionPlans(id)
    },
    {
      enabled: !!id && shouldFetch,
      initialData: !shouldFetch
        ? {
            subscriptions: initialPlans,
            campaign: initialCampaign
          }
        : undefined
    }
  )

  const promo = initialCampaign ?? data?.campaign ?? null
  const promoId: string | undefined = promo?.id

  const setSel = setSelected ?? setInternalSelected
  const currentSelected = selected ?? internalSelected

  const fallbackPlan: Plan | null =
    fallbackMonthlyPrice != null &&
    Number.isFinite(Number(fallbackMonthlyPrice)) &&
    Number(fallbackMonthlyPrice) > 0
      ? {
          id: '__fallback__',
          price: Number(fallbackMonthlyPrice),
          month_count: 1,
          name: 'Monthly',
          discount: 0
        }
      : null

const renderSubscriptions: Plan[] =
  initialPlans.length
    ? initialPlans
    : data?.subscriptions?.length
      ? data.subscriptions
      : fallbackPlan
        ? [fallbackPlan]
        : []

  useEffect(() => {
    if (fallbackPlan && currentSelected == null) {
      setSel(fallbackPlan.id)
    }
  }, [fallbackPlan?.id])

  useEffect(() => {
    if (!renderSubscriptions.length) return

    const valid = renderSubscriptions.some(p => p.id === currentSelected)

    if (currentSelected == null || !valid) {
      setSel(renderSubscriptions[0].id)
    }
  }, [renderSubscriptions.length])

  const selectedPlan =
    typeof currentSelected === 'string' &&
    currentSelected.endsWith('campaign') &&
    promo
      ? renderSubscriptions.find(
          p => p.id === currentSelected.replace(/campaign$/, '')
        )
      : renderSubscriptions.find(p => p.id === currentSelected)

  const monthlyPlan = renderSubscriptions.find(p => p.month_count === 1)

  const bundlePlans = renderSubscriptions
    .filter(p => p.month_count > 1)
    .sort((a, b) => a.month_count - b.month_count)

  const originalPrice = monthlyPlan?.price

  const dataWithMonthlyDiscount = (plan: Plan): Plan =>
    plan.month_count === 1 && promo?.discounted_price != null
      ? {
          ...plan,
          price: Number(promo.discounted_price)
        }
      : plan

  const handleSubscribe = () => {
    if (!selectedPlan || selectedPlan.id === '__fallback__') {
      addToast('error', t('error:anErrorOccuredPleaseTryAgain'))
      return
    }

    const effectivePlan =
      selectedPlan.month_count === 1 &&
      promo?.discounted_price != null
        ? {
            ...selectedPlan,
            price: Number(promo.discounted_price)
          }
        : selectedPlan

    modal.addModal(
      t('confirmSubscribtion'),
      <SubscribeConfirmModal
        avatar={avatar}
        displayName={displayName}
        username={username}
        subscribeFn={subscribeFn}
        selectedPlan={effectivePlan}
        campaign={selectedPlan.month_count === 1 ? promo : null}
        originalPrice={originalPrice ?? selectedPlan.price ?? 0}
        promoCampaignId={selectedPlan.month_count === 1 ? promoId : undefined}
      />
    )
  }

  return (
    <div
      className={`subscribe-profile-modal-wrapper ${
        bundlePlans.length > 0 ? 'subscribe-profile-modal-wrapper-has-bundles' : ''
      } ${bundlesShown ? 'subscribe-profile-modal-wrapper-bundles' : ''}`}
    >
      <div className='subscribe-profile-modal-container'>
        <div className='subscribe-profile-modal-info'>
          <ImgInCircle
            type='profile'
            hasLoader
            customClass='subscribe-profile-modal-avatar'
          >
            <img
              src={avatar ?? AllIcons.user_placeholder}
              alt={t('profileAvatar')}
            />
          </ImgInCircle>

          <div className='subscribe-profile-modal-info-name-container'>
            <div className='subscribe-profile-modal-info-name'>
              {displayName}
            </div>

            <div className='subscribe-profile-modal-info-name-username'>
              @{username}
            </div>
          </div>
        </div>

        <div className='subscribe-profile-modal-subscription-benefits-container'>
          <div className='subscribe-profile-modal-subscription-benefit'>
            <spriteIcons.IconCheckmarkGradient />
            <span className='subscibtion-benefit-text'>
              {t('fullAccessToThisUserContent')}
            </span>
          </div>

          <div className='subscribe-profile-modal-subscription-benefit'>
            <spriteIcons.IconCheckmarkGradient />
            <span className='subscibtion-benefit-text'>
              {t('directMessageWithThisUser')}
            </span>
          </div>

          <div className='subscribe-profile-modal-subscription-benefit'>
            <spriteIcons.IconCheckmarkGradient />
            <span className='subscibtion-benefit-text'>
              {t('cancelYourSubscriptionsAtAnyTime')}
            </span>
          </div>
        </div>

        {renderSubscriptions.map(plan =>
          plan.id === currentSelected ? (
            <SubscriptionCard
              key={plan.id}
              selected
              onClick={() => setSel(plan.id)}
              data={dataWithMonthlyDiscount(plan)}
              notInList
              campaign={plan.month_count === 1 ? promo : null}
              campaignDiscount={
                plan.month_count === 1 ? promo?.discount : undefined
              }
              free={plan.month_count === 1 ? promo?.free : undefined}
            />
          ) : (
            `${plan.id}campaign` === currentSelected &&
            promo && (
              <SubscriptionCard
                key={plan.id}
                selected
                onClick={() => null}
                data={dataWithMonthlyDiscount(plan)}
                notInList
                campaignDiscount={promo?.discount}
                free={promo?.free}
                campaign={promo}
              />
            )
          )
        )}

        {renderSubscriptions.length > 1 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10
            }}
          >
            <button
              className='subscription-profile-modal-bundles-button'
              onClick={() => {
                if (monthlyPlan) {
                  setSel(
                    promo
                      ? `${monthlyPlan.id}campaign`
                      : monthlyPlan.id
                  )

                  setCampaign?.(
                    promo
                      ? promoId ?? null
                      : null
                  )
                }

                setBundlesShown(true)
              }}
            >
              {t('viewBundles')}
            </button>
          </div>
        )}

        <button
          className={`subscription-profile-modal-subscribe-button ${
            isLoading
              ? 'subscription-profile-modal-subscribe-button-loading'
              : ''
          }`}
          onClick={handleSubscribe}
        >
          {t('subscribe')}
        </button>
      </div>

      <div className='subscribe-profile-modal-bundles-container'>
        <div className='subscribe-profile-modal-bundles-options'>
          {monthlyPlan &&
            (promo ? (
              <SubscriptionCard
                key={`${monthlyPlan.id}-campaign-top`}
                data={dataWithMonthlyDiscount(monthlyPlan)}
                selected={
                  String(currentSelected) === `${monthlyPlan.id}campaign` ||
                  String(currentSelected) === String(monthlyPlan.id)
                }
                onClick={() => {
                  setSel(`${monthlyPlan.id}campaign`)
                  setCampaign?.(promoId ?? null)
                }}
                campaignDiscount={promo?.discount}
                free={promo?.free}
                campaign={promo}
              />
            ) : (
              <SubscriptionCard
                key={`${monthlyPlan.id}-monthly-top`}
                data={monthlyPlan}
                selected={
                  String(currentSelected) === String(monthlyPlan.id)
                }
                onClick={() => {
                  setSel(monthlyPlan.id)
                  setCampaign?.(null)
                }}
              />
            ))}

          {bundlePlans.map(plan => (
            <SubscriptionCard
              key={plan.id}
              data={plan}
              selected={
                String(currentSelected) === String(plan.id) ||
                String(currentSelected) === `${plan.id}campaign`
              }
              onClick={() => {
                setSel(plan.id)
                setCampaign?.(null)
              }}
            />
          ))}
        </div>

        <button
          className='subscription-profile-modal-bundles-button subscribe-profile-modal-back-button'
          onClick={() => setBundlesShown(false)}
        >
          {t('back')}
        </button>

        <button
          className={`subscription-profile-modal-subscribe-button ${
            isLoading
              ? 'subscription-profile-modal-subscribe-button-loading'
              : ''
          }`}
          onClick={handleSubscribe}
        >
          {t('subscribe')}
        </button>
      </div>
    </div>
  )
}

export default SubscribeProfileModal